import Navbar from "./components/navbar/Navbar.tsx";
import Footer from "./components/Footer.tsx";
import { Navigate, Outlet, ScrollRestoration, useLocation } from "react-router";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useEffect, useState } from "react";
import { useSupportedLanguages } from "./api/languageApi.ts";
import { useSupportedInstruments } from "./api/instrumentApi.ts";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { findAndActivateLocale, initialize } from "./i18n.ts";
import { useLanguageStore } from "./stores/languageStore.ts";
import { Toaster } from "react-hot-toast";
import * as Sentry from "@sentry/react";
import FirstSetupWizard from "./views/FirstSetupWizard.tsx";
import AppBottomNav from "./components/AppBottomNav.tsx";

export const App = () => {
  const auth = useAuth();
  const location = useLocation();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [i18nReady, setI18nReady] = useState(false);
  const { setLocale, language } = useLanguageStore();
  const { refetch: refetchSupportedLanguages } = useSupportedLanguages();
  const { refetch: refetchSupportedInstruments } = useSupportedInstruments();

  useEffect(() => {
    if (auth.user) {
      Sentry.setUser({
        id: auth.user.profile.sub,
      });
    }
  }, [auth]);

  // initialize i18n and set initial language for navbar state
  useEffect(() => {
    initialize()
      .then((a) => {
        if (language) {
          findAndActivateLocale(language)
            .then((value) => {
              setLocale(value);
              setI18nReady(true);
            })
            .catch(console.error);
        } else {
          setLocale(a);
        }
      })
      .catch(console.error);
  }, [language, setLocale]);

  // workaround for https://github.com/authts/oidc-client-ts/issues/1601
  useEffect(() => {
    return auth.events.addAccessTokenExpired(() => {
      console.log("Manual signin after expiry");
      auth.signinSilent().catch(console.error);
    });
  }, [auth.events, auth.signinSilent]);

  useEffect(() => {
    return auth.events.addSilentRenewError(() => {
      console.log("Silent renew failed, redirecting to login");
      auth.signinRedirect().catch(console.error);
    });
  }, [auth.events, auth.signinRedirect]);

  // preload some data
  useEffect(() => {
    refetchSupportedLanguages().catch(console.error);
  }, [refetchSupportedLanguages]);
  useEffect(() => {
    refetchSupportedInstruments().catch(console.error);
  }, [refetchSupportedInstruments]);

  // try to refresh session when loading
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin
    ) {
      console.log("silent sign in");
      auth.signinSilent();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  // Remove trailing slashes in url
  if (location.pathname.match("/.*/$")) {
    return (
      <Navigate
        replace
        to={{
          pathname: location.pathname.replace(/\/+$/, ""),
          search: location.search,
        }}
      />
    );
  }

  if (!i18nReady) {
    return null;
  }

  return (
    <I18nProvider i18n={i18n}>
      <ScrollRestoration />
      <FirstSetupWizard />
      <div className={"bg-soluud-background flex min-h-screen flex-col"}>
        <a
          href="#main-content"
          className="focus:bg-soluud-primary sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
        >
          Skip to main content
        </a>
        <Navbar />
        <main
          id="main-content"
          className="page-enter flex flex-1 flex-col pb-16 sm:pb-0"
        >
          <Outlet />
        </main>
        <Footer />
        <AppBottomNav />
        <Toaster />
      </div>
    </I18nProvider>
  );
};
